"use client";

import { cn } from "@/lib/utils";

interface LoadingScreenProps {
  className?: string;
  message?: string;
  variant?: "full" | "compact";
}

/**
 * LoadingScreen / Splash - uses plain <img> only for maximum compatibility
 * with Android WebView and older devices. Avoids next/image and dynamic
 * detection that can fail before hydration.
 */
export function LoadingScreen({
  className,
  message,
  variant = "full",
}: LoadingScreenProps) {
  const logoElement = (
    <picture>
      <source srcSet="/logo-icon-tight.webp" type="image/webp" />
      <img
        src="/logo-icon-tight.png"
        alt="GrowthSync"
        className="h-full w-full object-contain"
        loading="eager"
        decoding="async"
              fetchPriority="high"
        width={256}
        height={256}
      />
    </picture>
  );

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "flex items-center justify-center p-4 min-h-[60vh]",
          className
        )}
      >
        <div
          className="relative h-20 w-20 md:h-28 md:w-28 lg:h-32 lg:w-32 animate-pulse"
          style={{ transform: "translateY(20%)" }}
        >
          {logoElement}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "fixed inset-0 flex items-center justify-center bg-white px-4 z-50 min-h-viewport-fixed",
        className
      )}
    >
      <div className="flex flex-col items-center justify-center w-full h-full min-h-viewport-fixed">
        <div className="relative h-20 w-20 md:h-28 md:w-28 lg:h-32 lg:w-32 animate-pulse">
          {logoElement}
        </div>
        {message && (
          <p className="text-sm md:text-base text-neutral-600 font-medium mt-6 text-center">
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

